import classNames from 'classnames'
import { FC } from 'react'
import Button, { ButtonProps } from '@mui/material/Button'
import { CustomIconFromValue } from '../CustomIconFromValue'
import { Typography, darken, lighten, styled } from '@mui/material'
import Link from 'next/link'
import { TranslateMessage } from '@app/lib/lang'
import { themeColors } from '@app/constants'
import { ConditionalWrapper } from '../ConditionalWrapper'

export interface CustomButtonProps extends Omit<ButtonProps, 'size'> {
	className?: string
	disabled?: boolean
	link?: string
	loading?: boolean
	size?: 'small' | 'medium' | 'large' | 'medium-large'
	type?: 'button' | 'submit'
	color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
	onClick?: () => void
	label?: string
	fontWeight?: number | string
	fontColor?: string
	children?: React.ReactNode
	minWidth?: string
}

export const CustomButton: FC<CustomButtonProps> = ({
	className,
	children,
	disabled = false,
	link,
	loading = false,
	size = 'large',
	type = 'button',
	color = 'primary',
	onClick,
	label,
	fontWeight = 'bold',
	fontColor,
	sx,
	minWidth,
	fullWidth,
	hidden,
	...otherProps
}) => {
	const onCustomButtonClick = () => {
		if (onClick) {
			onClick()
		}
	}

	const id =
		color === 'error' ? 'delete' : color === 'primary' ? 'save' : undefined

	if (hidden) return null

	return (
		<ConditionalWrapper
			condition={!!link}
			wrapper={(children) => (
				<Link href={link!} style={{ cursor: 'pointer' }}>
					{children}
				</Link>
			)}
		>
			<CustomButtonRoot
				minWidth={minWidth}
				disabled={loading || disabled}
				className={
					sx
						? ''
						: classNames(['customButton', size, color, className])
				}
				onClick={onCustomButtonClick}
				variant="contained"
				type={type}
				sx={{
					...sx,
					...(fullWidth && { minWidth: '100%' }),
					...(disabled && {
						opacity: 0.6,
						cursor: 'not-allowed',
					}),
				}}
				id={id}
				{...otherProps}
			>
				{loading ? (
					<CustomIconFromValue
						icon="svg loader"
						style={{
							height: '100%',
							// width: '!00%',
						}}
					/>
				) : (
					<Typography
						component="div"
						fontWeight={fontWeight}
						color={fontColor}
						fontSize={
							size === 'medium' || size === 'small' ? 12 : 14
						}
					>
						{label ? (
							<TranslateMessage id={label ?? ''} isTitle />
						) : (
							<>{children}</>
						)}
					</Typography>
				)}
			</CustomButtonRoot>
		</ConditionalWrapper>
	)
}

const CustomButtonRoot = styled(Button, {
	shouldForwardProp: (prop) => prop !== 'minWidth',
})<{ minWidth?: string }>(({ minWidth }) => ({
	borderRadius: '2px',
	boxShadow: 'none',
	minWidth: minWidth,
	'&.customButton': {
		color: 'white',
		borderRadius: '2px',
		boxShadow: 'none',
	},
	'&.small': {
		height: '28px',
		minWidth: '145px',
		fontSize: '0.6rem',
	},
	'&.medium': {
		height: '28px',
		minWidth: '145px',
		fontSize: '0.75rem',
	},
	'&.medium-large': {
		height: '34px',
		minWidth: '145px',
		fontSize: '0.75rem',
	},
	'&.large': {
		height: '48px',
		width: '175px',
		fontSize: '0.75rem',
	},
	'&.primary': {
		backgroundColor: themeColors.dark,
		'&:hover': {
			boxShadow: 'none',
			backgroundColor: lighten(themeColors.dark, 0.12),
		},
	},
	'&.secondary': {
		backgroundColor: themeColors.white,
		border: `2px solid ${themeColors.agitronDark}`,
		color: `${themeColors.agitronDark} !important`,
		'&:hover': {
			boxShadow: 'none',
			backgroundColor: darken(themeColors.white, 0.02),
		},
	},
	'&.error': {
		backgroundColor: themeColors.errorRed,
		'&:hover': {
			boxShadow: 'none',
			backgroundColor: lighten(themeColors.errorRed, 0.12),
		},
	},
	'&.info': {
		backgroundColor: themeColors.agitronLight,
		'&:hover': {
			boxShadow: 'none',
			backgroundColor: darken(themeColors.agitronLight, 0.06),
		},
	},
	'&.warning': {
		backgroundColor: themeColors.yellow,
		'&:hover': {
			boxShadow: 'none',
			backgroundColor: lighten(themeColors.yellow, 0.12),
		},
	},
}))
